import { createContext, useCallback, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IntentButton, type Intent } from "./intent-button";

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  intent?: Extract<Intent, "primary" | "destructive">;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(
  null,
);

// ConfirmProvider assumes a single root-level instance per app. If you nest a
// route-scoped ConfirmProvider, add a useEffect cleanup that resolves any
// pending confirm with `false` on unmount, otherwise an awaiting consumer
// will hang indefinitely.
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending((prev) => {
          if (prev !== null) {
            // Loud failure: a silent hang on the dropped first promise is much
            // worse to debug than a thrown error at the second call site.
            throw new Error("ConfirmProvider: a confirmation is already pending");
          }
          return { ...opts, resolve };
        });
      }),
    [],
  );

  const handleClose = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={pending !== null} onOpenChange={(o) => !o && handleClose(false)}>
        <DialogContent>
          {pending ? (
            <>
              <DialogHeader>
                <DialogTitle>{pending.title}</DialogTitle>
                {pending.body ? <DialogDescription>{pending.body}</DialogDescription> : null}
              </DialogHeader>
              <DialogFooter>
                <IntentButton intent="ghost" onClick={() => handleClose(false)}>
                  {pending.cancelLabel ?? "Cancel"}
                </IntentButton>
                <IntentButton
                  intent={pending.intent ?? "primary"}
                  onClick={() => handleClose(true)}
                >
                  {pending.confirmLabel ?? "Confirm"}
                </IntentButton>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
